import { ComponentType } from '@/components/ComponentDefs';
import { EventBus, GameEvents } from '@/events/EventBus';
import { COMPONENT_SPACING, ROBOT_TILE_SCALE, GAME_WIDTH, GAME_HEIGHT, TILE_SIZE } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import { SoundManager } from '@/systems/SoundManager';
import { RobotState } from '@/entities/Robot';
import type { RobotHost } from './RobotHost';
import type { RobotDamageSystem } from './RobotDamageSystem';

/**
 * Handles topple/fall physics, crash sequence, and stand-up for a Robot.
 */
export class TopplePhysics {
  // Topple physics state
  public toppleActive = false;
  public toppleAngVel = 0;
  public toppleAngle = 0;
  public topplePivotX = 0;
  public topplePivotY = 0;
  public toppleGroundY = 0;
  public toppleMaxAngle = Math.PI / 2;

  // Crash sequence state
  private crashTimer: number = 0;
  private crashDuration: number = 1.5;
  private crashStartX: number = 0;
  private crashStartY: number = 0;
  private crashTargetX: number = 0;
  private crashTargetY: number = 0;

  constructor(
    private host: RobotHost,
    private damageSystem: RobotDamageSystem,
  ) {}

  /** Robot tips over on one side */
  topple(fallDir: number): void {
    if (this.host.isFallen || !this.host.alive) return;
    this.host.isFallen = true;
    this.host.fallDirection = fallDir;
    this.host.state = RobotState.FALLEN;
    this.host.vx = 0;
    this.host.vy = 0;
    this.host.moveIntentSpeed = 0;

    const active = this.host.components.filter(c => !c.broken);
    if (active.length === 0) { this.toppleActive = false; return; }
    const sm = GOD.componentSpacingMult;
    const s = this.host.renderScale;

    // 1. Find bottom row (support base)
    const maxLocalY = Math.max(...active.map(c => c.localY));
    const bottomRow = active.filter(c => Math.abs(c.localY - maxLocalY) < COMPONENT_SPACING * 0.5);

    // 2. Pivot = most extreme component in fall direction within bottom row
    const pivotComp = fallDir > 0
      ? bottomRow.reduce((best, c) => c.localX > best.localX ? c : best, bottomRow[0])
      : bottomRow.reduce((best, c) => c.localX < best.localX ? c : best, bottomRow[0]);
    const pivotLX = pivotComp.localX;
    const pivotLY = pivotComp.localY;
    this.topplePivotX = pivotLX * sm;
    this.topplePivotY = pivotLY * sm;

    this.toppleGroundY = this.host.y + maxLocalY * sm * s + COMPONENT_SPACING * s * 0.5;

    // 3. Calculate max topple angle based on robot geometry
    let maxAngle = Math.PI / 2;
    for (const c of active) {
      if (c === pivotComp) continue;
      const dx = (c.localX - pivotLX) * fallDir;
      const dy = c.localY - pivotLY;
      if (dx > 1 && dy < -1) {
        const angle = Math.atan2(Math.abs(dy), dx);
        maxAngle = Math.min(maxAngle, angle);
      }
    }
    this.toppleMaxAngle = Math.max(maxAngle, 0.15);

    // 4. Start physics sim
    this.toppleActive = true;
    this.toppleAngle = 0;
    const flipSign = this.host.flipped ? -1 : 1;
    this.toppleAngVel = fallDir * flipSign * 0.5;

    this.host.recalcStats();
  }

  /**
   * Step topple physics. Call each frame with raw dt (ms).
   * Returns true while the topple is still animating.
   */
  updateTopplePhysics(dtMs: number): boolean {
    if (!this.host.isFallen) return false;

    if (!this.toppleActive) {
      this._applyToppleContainer();
      return false;
    }
    const dt = dtMs / 1000;
    const GRAVITY_ACCEL = 8.0;
    const DAMPING = 0.92;
    const MAX_ANGLE = this.toppleMaxAngle;

    const dir = this.host.fallDirection * (this.host.flipped ? -1 : 1);
    const angleFromUpright = this.toppleAngle;
    const torque = dir * GRAVITY_ACCEL * (1 + Math.abs(angleFromUpright) * 2);
    this.toppleAngVel += torque * dt;

    this.toppleAngle += this.toppleAngVel * dt;

    if (Math.abs(this.toppleAngle) >= MAX_ANGLE) {
      this.toppleAngle = dir * MAX_ANGLE;
      if (Math.abs(this.toppleAngVel) > 0.3) {
        this.toppleAngVel *= -0.3 * DAMPING;
      } else {
        this.toppleAngVel = 0;
        this.toppleActive = false;
        this.damageSystem.applyFallDamage(this.host.fallDirection, this.topplePivotX, this.topplePivotY, this.toppleMaxAngle);
        SoundManager.play('componentBreak');
        EventBus.emit(GameEvents.ROBOT_TOPPLED, this.host);
      }
    }

    this._applyToppleContainer();
    return true;
  }

  /** Update container rotation/position based on current topple angle and pivot */
  private _applyToppleContainer(): void {
    const s = this.host.renderScale;
    const flipX = this.host.flipped ? -1 : 1;
    const pvx = this.topplePivotX * s * flipX;
    const pvy = this.topplePivotY * s;

    const cosA = Math.cos(this.toppleAngle);
    const sinA = Math.sin(this.toppleAngle);
    const offsetX = pvx - (pvx * cosA - pvy * sinA);
    const offsetY = pvy - (pvx * sinA + pvy * cosA);

    this.host.container.setRotation(this.toppleAngle);
    this.host.container.setPosition(this.host.x + offsetX, this.host.y + offsetY);
  }

  /** Restore robot to upright position */
  standUp(): void {
    if (!this.host.isFallen) return;
    this.host.isFallen = false;
    this.host.fallDirection = 0;
    this.toppleActive = false;
    this.toppleAngle = 0;
    this.toppleAngVel = 0;
    this.toppleMaxAngle = Math.PI / 2;
    this.host.state = RobotState.IDLE;
    this.host.container.setRotation(0);
    this.host.container.setPosition(this.host.x, this.host.y);
    this.host.scene.tweens.add({
      targets: this.host.container,
      rotation: 0,
      duration: 300,
      ease: 'Back.easeOut',
    });
    this.host.recalcStats();
  }

  // ── Flying crash sequence ──

  /** Start crash sequence for an unstable flying robot. */
  startCrash(): void {
    if (this.host.isCrashing || !this.host.alive) return;
    this.host.isCrashing = true;
    this.crashTimer = 0;
    this.crashStartX = this.host.x;
    this.crashStartY = this.host.y;

    // Pick a crash landing spot 2-4 tiles away in a random direction
    const dist = TILE_SIZE * (2 + Math.random() * 2);
    const angle = Math.random() * Math.PI * 2;
    const margin = TILE_SIZE;
    this.crashTargetX = Math.max(margin, Math.min(GAME_WIDTH - margin, this.host.x + Math.cos(angle) * dist));
    this.crashTargetY = Math.max(margin, Math.min(GAME_HEIGHT - margin, this.host.y + Math.sin(angle) * dist));

    this.host.state = RobotState.FALLEN;
    this.host.moveIntentSpeed = 0;
    SoundManager.play('componentBreak');
    EventBus.emit(GameEvents.ROBOT_CRASHING, this.host);
  }

  /**
   * Step the crash physics each frame.
   * @param dt delta time in seconds
   * @returns true while still spiraling, false when impact happened
   */
  updateCrashPhysics(dt: number): boolean {
    if (!this.host.isCrashing || !this.host.alive) return false;

    this.crashTimer += dt;
    const progress = Math.min(1, this.crashTimer / this.crashDuration);

    // Spin accelerates as robot loses control
    this.host.spinRate = 2 + progress * 10;
    this.host.heading += this.host.spinRate * dt;
    while (this.host.heading > Math.PI) this.host.heading -= Math.PI * 2;
    while (this.host.heading < -Math.PI) this.host.heading += Math.PI * 2;

    // Spiral toward nearby crash target using eased interpolation + wobble
    const eased = progress * progress; // quadratic ease-in for accelerating descent feel
    this.host.x = this.crashStartX + (this.crashTargetX - this.crashStartX) * eased
      + Math.sin(progress * 8) * TILE_SIZE * 0.3 * (1 - progress); // wobble fades out
    this.host.y = this.crashStartY + (this.crashTargetY - this.crashStartY) * eased
      + Math.cos(progress * 8) * TILE_SIZE * 0.2 * (1 - progress);

    const tiltAngle = progress * 0.4;
    this.host.container.setRotation(this.host.heading + tiltAngle);
    this.host.container.setPosition(this.host.x, this.host.y);

    if (Math.random() < 0.3 + progress * 0.5) {
      this.spawnCrashSmoke();
    }

    if (progress >= 1) {
      // Snap to crash target
      this.host.x = this.crashTargetX;
      this.host.y = this.crashTargetY;
      this.crashImpact();
      return false;
    }

    return true;
  }

  /** Spawn smoke puff behind the crashing robot */
  private spawnCrashSmoke(): void {
    const scene = this.host.scene;
    if (!scene || !scene.scene.isActive()) return;
    const hasTex = scene.textures.exists('fx_smoke');
    const s = ROBOT_TILE_SCALE;
    const ox = (Math.random() - 0.5) * 20 * s;
    const oy = (Math.random() - 0.5) * 20 * s;
    const puff = hasTex
      ? scene.add.image(this.host.x + ox, this.host.y + oy, 'fx_smoke').setTint(0x444444).setAlpha(0.6).setScale(s * 0.5).setDepth(5)
      : scene.add.circle(this.host.x + ox, this.host.y + oy, 4 * s, 0x444444, 0.6).setDepth(5);
    scene.tweens.add({
      targets: puff,
      scaleX: s * 2, scaleY: s * 2,
      alpha: 0,
      duration: 400 + Math.random() * 200,
      onComplete: () => puff.destroy(),
    });
  }

  /** Final crash impact */
  private crashImpact(): void {
    SoundManager.play('explosion');
    const scene = this.host.scene;
    const s = ROBOT_TILE_SCALE;
    const flash = scene.add.circle(this.host.x, this.host.y, 30 * s, 0xff6600, 0.9).setDepth(200);
    scene.tweens.add({
      targets: flash,
      scaleX: 3, scaleY: 3, alpha: 0,
      duration: 300, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
    scene.cameras.main?.shake(200, 0.01);
    this.host.destroy();
  }
}
